'use client';

import type React from 'react';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Input } from 'src/components/ui/input'; // Revertido para o caminho original
import { Label } from 'src/components/ui/label'; // Revertido para o caminho original
import { Button } from 'src/components/Button'; // Revertido para o caminho original
import { api } from 'src/services/api';
import type Client from 'src/model/User';
import AutocompleteBank from 'src/components/AutoCompleteBank/AutoCompleteBank';

function formatCNPJ(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
}

const formatCurrency = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (!numbers) return '';
  const numberValue = Number.parseInt(numbers, 10) / 100;
  return numberValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseCurrencyToNumber = (value: string): number => {
  if (!value) return 0;
  const cleanValue = value.replace(/\./g, '').replace(',', '.');
  return Number.parseFloat(cleanValue) || 0;
};

const prettifyLabel = (key: string) => {
  const labels: Record<string, string> = {
    email: 'E-mail',
    companyName: 'Razão Social',
    tradingName: 'Nome Fantasia',
    documento: 'Número do CNPJ',
    monthlyIncome: 'Renda Mensal',
    annualRevenue: 'Faturamento Anual',
    professionalOccupation: 'Profissão ou Ocupação',
    cellPhone: 'Celular',
    type: 'Tipo de Pessoa',
    emailUserSameUser: 'Usar o mesmo e-mail do usuário logado',
    phoneUserSameUser: 'Usar o mesmo telefone do usuário logado',
    addressUserSameMain: 'Usar o mesmo endereço principal do seller',
    addressId: 'Endereço Selecionado',
    foundingDate: 'Data de Fundação',
    partnerMotherName: 'Nome da Mãe do Sócio',
    corporationType: 'Tipo de Corporação',
    'defaultBankAccount.bank': 'Banco',
    'defaultBankAccount.branch_number': 'Agência',
    'defaultBankAccount.branch_check_digit': 'Dígito da Agência',
    'defaultBankAccount.account_number': 'Número da Conta',
    'defaultBankAccount.account_check_digit': 'Dígito da Conta',
    'defaultBankAccount.type': 'Tipo de Conta',
  };
  return (
    labels[key] ||
    key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

export const SellFormScreen: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    type: 'PF',
    companyName: '',
    tradingName: '',
    monthlyIncome: '',
    annualRevenue: '',
    professionalOccupation: '',
    cellPhone: '',
    emailUserSameUser: true,
    phoneUserSameUser: true,
    addressUserSameMain: true,
    addressId: '',
    documento: '',
    foundingDate: '',
    partnerMotherName: '',
    corporationType: '',
    address: {
      street: '',
      complementary: '',
      street_number: '',
      neighborhood: '',
      city: '',
      state: '',
      zip_code: '',
      reference_point: '',
    },
    mainAddress: {
      street: '',
      complementary: '',
      street_number: '',
      neighborhood: '',
      city: '',
      state: '',
      zip_code: '',
      reference_point: '',
    },
    defaultBankAccount: {
      bank: '',
      branch_number: '',
      branch_check_digit: '',
      account_number: '',
      account_check_digit: '',
      type: '',
    },
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [bancos, setBancos] = useState<
    { fullName: string; name: string; code: number }[]
  >([]);

  useEffect(() => {
    const fetchBancos = async () => {
      try {
        const res = await axios.get('https://brasilapi.com.br/api/banks/v1');
        setBancos(res.data);
      } catch (error) {
        console.error('Erro ao buscar bancos', error);
      }
    };
    fetchBancos();
  }, []);

  const [addresses, setAddresses] = useState<
    {
      id: string;
      street: string;
      number: string;
      city: string;
      state: string;
    }[]
  >([]);

  useEffect(() => {
    async function fetchAddresses() {
      try {
        const response = await api.get('/user/address');
        setAddresses(response.data);
      } catch (err) {
        console.error('Erro ao buscar endereços:', err);
      }
    }
    fetchAddresses();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'addressId' && value) {
      setAddressError(null);
    }
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleNumericInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string
  ) => {
    const inputValue = e.target.value;
    const sanitizedValue = inputValue.replace(/\D/g, ''); // Remove non-digits
    const [parent, child] = fieldName.split('.');

    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...(prev as any)[parent],
        [child]: sanitizedValue,
      },
    }));
  };

  const handleCurrencyChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string
  ) => {
    const inputValue = e.target.value;
    const formattedValue = formatCurrency(inputValue);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: formattedValue,
    }));
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (name === 'addressUserSameMain' && !checked) {
      setAddressError(null);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const validateForm = () => {
    if (formData.addressUserSameMain && !formData.addressId) {
      setAddressError(
        'Você deve selecionar um endereço quando "Usar o mesmo endereço principal do seller" estiver marcado.'
      );
      return false;
    }
    setAddressError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setSuccess(null);
    const cliente = await api.get<Client>(`/user/findeone`);
    const sanitizeNumber = (value: string) => value.replace(/[.\-/()\s]/g, '');
    const payload: any = {
      ...formData,
      document: formData.type === 'PF' ? cliente.data.cpf : formData.documento,
      type: formData.type,
      name: cliente.data.name,
      email: formData.emailUserSameUser ? cliente.data.email : formData.email,
      emailUserSameUser: formData.emailUserSameUser ? 1 : 0,
      phoneUserSameUser: formData.phoneUserSameUser ? 1 : 0,
      addressUserSameMain: formData.addressUserSameMain ? 1 : 0,
      cellPhone: formData.phoneUserSameUser
        ? cliente.data.cellPhone.replace(/[\s(),-]/g, '')
        : formData.cellPhone.replace(/[\s(),-]/g, ''),
      monthlyIncome: parseCurrencyToNumber(formData.monthlyIncome),
      annualRevenue: parseCurrencyToNumber(formData.annualRevenue),
    };

    // Sanitize bank account numbers before sending
    payload.defaultBankAccount.branch_number = sanitizeNumber(
      payload.defaultBankAccount.branch_number
    );
    payload.defaultBankAccount.branch_check_digit = sanitizeNumber(
      payload.defaultBankAccount.branch_check_digit
    );
    payload.defaultBankAccount.account_number = sanitizeNumber(
      payload.defaultBankAccount.account_number
    );
    payload.defaultBankAccount.account_check_digit = sanitizeNumber(
      payload.defaultBankAccount.account_check_digit
    );

    if (formData.addressUserSameMain) {
      delete payload.address;
      delete payload.mainAddress;
    }
    if (formData.type === 'PF') {
      delete payload.documento;
      delete payload.companyName;
      delete payload.tradingName;
      delete payload.annualRevenue;
      delete payload.foundingDate;
      delete payload.partnerMotherName;
      delete payload.corporationType;
    }
    console.log(payload);
    try {
      const response = await api.post('/seller/', payload);
      const ok = response.status === 200 || response.status === 201;
      setSuccess(
        ok ? 'Cadastro enviado com sucesso!' : 'Erro ao enviar os dados.'
      );
      if (ok) window.location.reload();
    } catch (error) {
      console.error('Erro na API:', error);
      setSuccess('Erro de rede ou servidor.');
    } finally {
      setLoading(false);
    }
  };

  const isPF = formData.type === 'PF';
  const isPJ = formData.type === 'PJ';

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-6">
            <h2 className="text-3xl font-bold text-white">
              Cadastro para Vender
            </h2>
            <p className="text-red-100 mt-2">
              Preencha os dados abaixo para começar a vender na plataforma
            </p>
          </div>
          <form onSubmit={handleSubmit} className="p-8">
            <div className="mb-10">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  <span className="text-red-600 font-bold">1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Tipo de Cadastro
                </h3>
              </div>
              <div className="flex space-x-4 mb-6">
                <div
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, type: 'PF' }))
                  }
                  className={`flex-1 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.type === 'PF'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        formData.type === 'PF'
                          ? 'border-red-500'
                          : 'border-gray-400'
                      }`}
                    >
                      {formData.type === 'PF' && (
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      )}
                    </div>
                    <span className="font-medium text-gray-800">
                      Pessoa Física
                    </span>
                  </div>
                </div>
                <div
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, type: 'PJ' }))
                  }
                  className={`flex-1 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.type === 'PJ'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                        formData.type === 'PJ'
                          ? 'border-red-500'
                          : 'border-gray-400'
                      }`}
                    >
                      {formData.type === 'PJ' && (
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      )}
                    </div>
                    <span className="font-medium text-gray-800">
                      Pessoa Jurídica
                    </span>
                  </div>
                </div>
              </div>
              <input
                type="hidden"
                name="type"
                value={formData.type}
                onChange={handleChange}
              />
            </div>
            <div className="mb-10">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  <span className="text-red-600 font-bold">2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Informações {isPF ? 'Pessoais' : 'da Empresa'}
                </h3>
              </div>
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-3 mb-6">
                    {[
                      ['emailUserSameUser', prettifyLabel('emailUserSameUser')],
                      ['phoneUserSameUser', prettifyLabel('phoneUserSameUser')],
                    ].map(([name, label]) => (
                      <div key={name} className="flex items-center">
                        <div
                          onClick={() =>
                            handleCheckbox({
                              target: {
                                name: name as string,
                                checked: !(formData as any)[name],
                              },
                            } as React.ChangeEvent<HTMLInputElement>)
                          }
                          className={`w-5 h-5 rounded border cursor-pointer flex items-center justify-center ${
                            (formData as any)[name]
                              ? 'bg-red-500 border-red-500'
                              : 'border-gray-300'
                          }`}
                        >
                          {(formData as any)[name] && (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="3"
                                d="M5 13l4 4L19 7"
                              ></path>
                            </svg>
                          )}
                        </div>
                        <label
                          className="ml-3 text-gray-700 cursor-pointer"
                          onClick={() =>
                            handleCheckbox({
                              target: {
                                name: name as string,
                                checked: !(formData as any)[name],
                              },
                            } as React.ChangeEvent<HTMLInputElement>)
                          }
                        >
                          {label}
                        </label>
                        <input
                          type="checkbox"
                          id={name}
                          name={name}
                          checked={(formData as any)[name]}
                          onChange={handleCheckbox}
                          className="hidden"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="form-group">
                    <Label
                      htmlFor="monthlyIncome"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {prettifyLabel('monthlyIncome')}
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R$</span>
                      </div>
                      <Input
                        id="monthlyIncome"
                        name="monthlyIncome"
                        inputMode="numeric"
                        value={formData.monthlyIncome}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleCurrencyChange(e, 'monthlyIncome')
                        }
                        placeholder="0,00"
                        className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {!formData.emailUserSameUser && (
                      <div className="form-group">
                        <Label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {prettifyLabel('email')}
                        </Label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                              className="h-5 w-5 text-gray-400"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                          </div>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                          />
                        </div>
                      </div>
                    )}
                    {!formData.phoneUserSameUser && (
                      <div className="form-group">
                        <Label
                          htmlFor="cellPhone"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {prettifyLabel('cellPhone')}
                        </Label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                              className="h-5 w-5 text-gray-400"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                            </svg>
                          </div>
                          <Input
                            id="cellPhone"
                            name="cellPhone"
                            value={formData.cellPhone}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const input = e.target as HTMLInputElement;
                              const sanitizedValue = input.value.replace(
                                /\D/g,
                                ''
                              );
                              const syntheticEvent = {
                                ...e,
                                target: {
                                  ...e.target,
                                  value: sanitizedValue,
                                  name: 'cellPhone',
                                },
                              };
                              handleChange(
                                syntheticEvent as React.ChangeEvent<HTMLInputElement>
                              );
                            }}
                            className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                          />
                        </div>
                      </div>
                    )}
                    {isPJ && (
                      <>
                        <div className="form-group">
                          <Label
                            htmlFor="documento"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel('documento')}
                          </Label>
                          <Input
                            id="documento"
                            name="documento"
                            inputMode="numeric"
                            value={formData.documento}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const formatted = formatCNPJ(e.target.value);
                              setFormData((prev) => ({
                                ...prev,
                                documento: formatted,
                              }));
                            }}
                            placeholder="00.000.000/0000-00"
                            required
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                          />
                        </div>
                        <div className="form-group">
                          <Label
                            htmlFor="companyName"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel('companyName')}
                          </Label>
                          <Input
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const input = e.target as HTMLInputElement;
                              const sanitizedValue = input.value.replace(
                                /[^a-zA-ZÀ-ÿ\s]/g,
                                ''
                              );
                              const syntheticEvent = {
                                ...e,
                                target: {
                                  ...e.target,
                                  value: sanitizedValue,
                                  name: 'companyName',
                                },
                              };
                              handleChange(
                                syntheticEvent as React.ChangeEvent<HTMLInputElement>
                              );
                            }}
                            required
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                          />
                        </div>
                        <div className="form-group">
                          <Label
                            htmlFor="tradingName"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel('tradingName')}
                          </Label>
                          <Input
                            id="tradingName"
                            name="tradingName"
                            value={formData.tradingName}
                            onChange={handleChange}
                            required
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                          />
                        </div>
                        <div className="form-group">
                          <Label
                            htmlFor="annualRevenue"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel('annualRevenue')}
                          </Label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500">R$</span>
                            </div>
                            <Input
                              id="annualRevenue"
                              name="annualRevenue"
                              value={formData.annualRevenue}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => handleCurrencyChange(e, 'annualRevenue')}
                              placeholder="0,00"
                              required
                              className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <Label
                            htmlFor="foundingDate"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel('foundingDate')}
                          </Label>
                          <Input
                            id="foundingDate"
                            name="foundingDate"
                            type="date"
                            value={formData.foundingDate}
                            onChange={handleChange}
                            required
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                          />
                        </div>
                        <div className="form-group">
                          <Label
                            htmlFor="partnerMotherName"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel('partnerMotherName')}
                          </Label>
                          <Input
                            id="partnerMotherName"
                            name="partnerMotherName"
                            value={formData.partnerMotherName}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const input = e.target as HTMLInputElement;
                              const sanitizedValue = input.value.replace(
                                /[^a-zA-ZÀ-ÿ\s]/g,
                                ''
                              );
                              const syntheticEvent = {
                                ...e,
                                target: {
                                  ...e.target,
                                  value: sanitizedValue,
                                  name: 'partnerMotherName',
                                },
                              };
                              handleChange(
                                syntheticEvent as React.ChangeEvent<HTMLInputElement>
                              );
                            }}
                            required
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                          />
                        </div>
                        <div className="form-group">
                          <Label
                            htmlFor="corporationType"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel('corporationType')}
                          </Label>
                          <select
                            id="corporationType"
                            name="corporationType"
                            value={formData.corporationType}
                            onChange={handleChange}
                            required
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition bg-white"
                          >
                            <option value="">Selecione o tipo</option>
                            <option value="LTDA">LTDA</option>
                            <option value="MEI">MEI</option>
                            <option value="SA">SA</option>
                            <option value="EIRELI">EIRELI</option>
                            <option value="ONG">ONG</option>
                            <option value="SS">SS</option>
                            <option value="COOPERATIVA">COOPERATIVA</option>
                            <option value="SLU">SLU</option>
                            <option value="EI">EI</option>
                          </select>
                        </div>
                      </>
                    )}
                    <div className="form-group">
                      <Label
                        htmlFor="professionalOccupation"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {prettifyLabel('professionalOccupation')}
                      </Label>
                      <Input
                        id="professionalOccupation"
                        name="professionalOccupation"
                        value={formData.professionalOccupation}
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const input = e.target as HTMLInputElement;
                          input.value = input.value
                            .replace(/[^a-zA-Z\s]/g, '')
                            .slice(0, 25);
                        }}
                        onChange={handleChange}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-10">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  <span className="text-red-600 font-bold">3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Endereço</h3>
              </div>
              <div className="space-y-6">
                <div className="form-group">
                  <div className="flex items-center mb-4">
                    <div
                      onClick={() =>
                        handleCheckbox({
                          target: {
                            name: 'addressUserSameMain',
                            checked: !formData.addressUserSameMain,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      className={`w-5 h-5 rounded border cursor-pointer flex items-center justify-center ${
                        formData.addressUserSameMain
                          ? 'bg-red-500 border-red-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {formData.addressUserSameMain && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="3"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      )}
                    </div>
                    <label
                      className="ml-3 text-gray-700 cursor-pointer"
                      onClick={() =>
                        handleCheckbox({
                          target: {
                            name: 'addressUserSameMain',
                            checked: !formData.addressUserSameMain,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    >
                      {prettifyLabel('addressUserSameMain')}
                    </label>
                    <input
                      type="checkbox"
                      id="addressUserSameMain"
                      name="addressUserSameMain"
                      checked={formData.addressUserSameMain}
                      onChange={handleCheckbox}
                      className="hidden"
                    />
                  </div>
                  <Label
                    htmlFor="addressId"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {prettifyLabel('addressId')}
                  </Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <select
                      id="addressId"
                      name="addressId"
                      value={formData.addressId}
                      onChange={handleChange}
                      className={`pl-10 w-full rounded-lg shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition appearance-none bg-white ${
                        addressError
                          ? 'border-red-500 border-2'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="">Selecione um endereço</option>
                      {addresses.map((addr) => (
                        <option key={addr.id} value={addr.id}>
                          {`${addr.street}, ${addr.number} - ${addr.city}/${addr.state}`}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                  {addressError && (
                    <div className="mt-2 flex items-center text-red-600 text-sm">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{addressError}</span>
                    </div>
                  )}
                </div>
                {!formData.addressUserSameMain && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
                    <h4 className="font-medium text-gray-700 mb-4">
                      Endereço Alternativo
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(formData.mainAddress).map(([key]) => (
                        <div key={`mainAddress.${key}`} className="form-group">
                          <Label
                            htmlFor={`mainAddress.${key}`}
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel(key)}
                          </Label>
                          <Input
                            id={`mainAddress.${key}`}
                            name={`mainAddress.${key}`}
                            value={(formData.mainAddress as any)[key]}
                            onChange={handleChange}
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mb-10">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  <span className="text-red-600 font-bold">4</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Conta Bancária
                </h3>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(formData.defaultBankAccount).map(([key]) => {
                    const fieldName = `defaultBankAccount.${key}`;
                    if (key === 'bank') {
                      return (
                        <div key={fieldName} className="form-group">
                          <AutocompleteBank
                            value={(formData.defaultBankAccount as any)[key]}
                            onChange={(val) =>
                              setFormData((prev) => ({
                                ...prev,
                                defaultBankAccount: {
                                  ...prev.defaultBankAccount,
                                  bank: val,
                                },
                              }))
                            }
                          />
                        </div>
                      );
                    }
                    if (key === 'type') {
                      return (
                        <div key={fieldName} className="form-group">
                          <Label
                            htmlFor={fieldName}
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {prettifyLabel(fieldName)}
                          </Label>
                          <select
                            id={fieldName}
                            name={fieldName}
                            value={(formData.defaultBankAccount as any)[key]}
                            onChange={handleChange}
                            className="w-full rounded-sm border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition bg-gray-100"
                          >
                            <option value="">Selecione o tipo</option>
                            <option value="corrente">Corrente</option>
                            <option value="poupanca">Poupança</option>
                          </select>
                        </div>
                      );
                    }
                    // Apply numeric input restrictions to these fields
                    const isNumericField = [
                      'branch_number',
                      'branch_check_digit',
                      'account_number',
                      'account_check_digit',
                    ].includes(key);

                    return (
                      <div key={fieldName} className="form-group">
                        <Label
                          htmlFor={fieldName}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {prettifyLabel(fieldName)}
                        </Label>
                        <Input
                          id={fieldName}
                          name={fieldName}
                          value={(formData.defaultBankAccount as any)[key]}
                          onChange={
                            isNumericField
                              ? (e: React.ChangeEvent<HTMLInputElement>) =>
                                  handleNumericInputChange(e, fieldName)
                              : handleChange
                          }
                          inputMode={isNumericField ? 'numeric' : undefined}
                          pattern={isNumericField ? '[0-9]*' : undefined}
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 transition"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="mt-10 flex flex-col items-center">
              <Button
                type="submit"
                title={loading ? 'Enviando...' : 'Salvar Cadastro'}
                buttonStyle="red"
                size="md"
                className={`w-full md:w-1/2 py-3 text-lg font-medium rounded-lg transition-all transform hover:scale-105 ${
                  loading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              />
              {success && (
                <div
                  className={`mt-6 w-full p-4 rounded-lg text-center ${
                    success.includes('sucesso')
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-center">
                    {success.includes('sucesso') ? (
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    )}
                    <span className="font-medium">{success}</span>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
